import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MaterialIcon from './MaterialIcon';
import {
  iconStyleColorToSx,
  iconStyleSizeToGridFontSize,
} from './iconPicker';
import {
  filterMaterialIconIds,
  materialIconTagName,
} from './materialIconRegistry';

interface MaterialIconGridProps {
  selectedIcon?: string;
  onSelect: (iconId: string) => void;
  previewColor?: string;
  previewSize?: string;
  maxHeight?: number;
}

export default function MaterialIconGrid({
  selectedIcon = '',
  onSelect,
  previewColor = 'primary',
  previewSize = 'medium',
  maxHeight = 400,
}: MaterialIconGridProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredIcons = useMemo(
    () => filterMaterialIconIds(searchTerm),
    [searchTerm],
  );

  const iconSx = {
    fontSize: iconStyleSizeToGridFontSize(previewSize),
    color: previewColor.includes('.') ? previewColor : iconStyleColorToSx(previewColor),
  };

  const showSearchHint = !searchTerm.trim();

  return (
    <Box>
      <TextField
        fullWidth
        label={t('editor.iconSelector.search')}
        placeholder={t('editor.iconSelector.searchMaterialHint')}
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        sx={{ mb: 1 }}
        slotProps={{
          input: {
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          },
        }}
      />
      {showSearchHint ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {t('editor.iconSelector.materialSearchHint', { count: filteredIcons.length })}
        </Typography>
      ) : null}
      <Box sx={{ maxHeight, overflow: 'auto' }}>
        <Grid container spacing={1}>
          {filteredIcons.map(iconId => {
            const tagLabel = materialIconTagName(iconId);
            return (
              <Grid size={{ xs: 4, sm: 3, md: 2 }} key={iconId}>
                <Tooltip title={tagLabel} placement="top">
                  <Box
                    sx={{
                      p: 1.25,
                      minHeight: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: selectedIcon === iconId ? 2 : 1,
                      borderColor: selectedIcon === iconId ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      cursor: 'pointer',
                      backgroundColor:
                        selectedIcon === iconId ? 'action.selected' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                    onClick={() => onSelect(iconId)}
                  >
                    <MaterialIcon iconId={iconId} sx={iconSx} />
                  </Box>
                </Tooltip>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
}
